// Admin re-run path for the first-run setup wizard (item 76 of
// PRODUCT-ROADMAP.md). The fresh-install auto-redirect path lives at
// /setup and uses its own starfield-backed shell; this page wraps the
// same SetupWizard component in the AdminShell so an admin can walk
// through the steps again without bouncing the auto-redirect bit.
//
// The "Reset wizard state" action clears every timestamp on
// system_setup so the public-config endpoint flips
// `setup_required: true` again, useful when an admin wants to
// rehearse the on-boarding flow before a customer demo.

import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CheckCircle2, RotateCcw, AlertTriangle, Loader2 } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { SetupWizard } from '../SetupWizard';
import { useSetupStatus, resetSetup } from '../../api/setup';
import { usePublicConfig } from '../../api/publicConfig';

export function AdminSystemSetup() {
  const { t } = useTranslation();
  const { data: status, mutate: mutateStatus } = useSetupStatus();
  const { mutate: mutatePubConfig } = usePublicConfig();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const onReset = async () => {
    if (!confirm(t('adminSystemSetup.confirmReset'))) return;
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await resetSetup();
      await Promise.all([mutateStatus(), mutatePubConfig()]);
      setMsg(t('adminSystemSetup.resetSuccess'));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const completed = !!status?.setup_completed_at;

  return (
    <AdminShell
      title={t('adminSystemSetup.page.title')}
      description={t('adminSystemSetup.page.description')}
    >
      <Card>
        <CardHeader title={t('adminSystemSetup.state.cardTitle')} />
        <CardBody className="space-y-3">
          <div className="text-sm text-fg-muted flex items-center gap-2">
            {completed ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-success" />
                {t('adminSystemSetup.state.completedLabel')}{' '}
                <span className="font-mono">
                  {status?.setup_completed_at
                    ? new Date(status.setup_completed_at).toISOString().slice(0, 16).replace('T', ' ')
                    : ''}
                </span>{' '}
                {t('adminSystemSetup.state.utc')}
                {status?.completed_by ? (
                  <>
                    {' '}{t('adminSystemSetup.state.completedBy')} <span className="font-mono">{status.completed_by}</span>
                  </>
                ) : null}
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 text-warning" />
                <Trans
                  i18nKey="adminSystemSetup.state.notCompleted"
                  components={[<span key="0" className="font-mono" />]}
                />
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={onReset} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              {busy ? t('adminSystemSetup.resetting') : t('adminSystemSetup.resetButton')}
            </Button>
            {msg && <span className="text-xs text-success">{msg}</span>}
            {err && <span className="text-xs text-danger font-mono break-all">{err}</span>}
          </div>
        </CardBody>
      </Card>
      <div className="mt-6">
        <SetupWizard embedded />
      </div>
    </AdminShell>
  );
}
